import React, { useEffect, useState, useContext } from "react";
import axios from "../utils/axiosInstance";
import { UserContext } from "../utils/userContext";
import { useT } from "../contexts/TranslationContext";
import PageWrapper from "../components/ui/PageWrapper";
import PageHeader from "../components/ui/PageHeader";
import Button from "../components/ui/Button";
import NewItemButton from "../components/ui/NewItemButton";
import { useBackNavigation } from "../navigation/BackNavigationContext";
import AddProjectModal from "../components/AddProjectModal";
import ProjectCard from "../components/ProjectCard";
import { getProjectLastViewed } from "../utils/projectViewTracking";

/**
 * ProjectsListView
 * - Lists projects (server-side pagination)
 * - Debounced search by name/address/city
 * - Shows "Create project" button for admins
 *
 * Uses shared components (PageWrapper, Button, Card) for consistent UI.
 */

const ProjectsListView = () => {
  const { user } = useContext(UserContext);
  const { t } = useT();
  const { markAsRoot } = useBackNavigation();

  useEffect(() => {
    markAsRoot(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState(search);
  const [projects, setProjects] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // debounce search input (300ms)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    fetchProjects(debounced, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced, page]);

  const fetchProjects = async (q = "", p = 1) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get("/projects", {
        params: { search: q, page: p, limit },
      });
      setProjects(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error("Error fetching projects", err);
      setError(t("projects.failedToLoad"));
    } finally {
      setLoading(false);
    }
  };

  const handleAddProject = () => {
    // Refresh the projects list after adding
    fetchProjects(debounced, page);
  };

  const onPrev = () => setPage((s) => Math.max(1, s - 1));
  const onNext = () => setPage((s) => s + 1);

  // helper: join address and city into a single readable string
  const formatAddressCity = (proj) =>
    [proj.address, proj.city].filter(Boolean).join(", ");

  // helper: construct Google Maps search URL for the address/city
  const mapsHref = (proj) => {
    const loc = formatAddressCity(proj);
    return loc
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
          loc
        )}`
      : null;
  };

  const totalPages = Math.ceil(total / limit);

  const getStatusBadge = (proj) => {
    // Prefer backend-provided status when available; fall back to client-side computation
    const status = proj && proj.status;

    if (status === "archived" || proj.isDeleted) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-sm bg-amber-700 text-white">
          {t("projects.statusArchived")}
        </span>
      );
    }

    if (status === "done") {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-sm bg-green-700 text-white">
          {t("projects.statusDone")}
        </span>
      );
    }

    if (status === "upcoming") {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-sm bg-gray-200 text-gray-800">
          {t("projects.statusUpcoming")}
        </span>
      );
    }

    if (status === "in_progress") {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-sm bg-green-100 text-green-800">
          {t("projects.statusInProgress")}
        </span>
      );
    }

    // Fallback: compute from dates if backend status not present
    const now = new Date();
    const start = proj.startDate ? new Date(proj.startDate) : null;
    const end = proj.endDate ? new Date(proj.endDate) : null;

    // Done: has an end date in the past
    if (end && end < now) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-sm bg-green-700 text-white">
          {t("projects.statusDone")}
        </span>
      );
    }

    // Upcoming: start date in the future
    if (start && start > now) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-sm bg-gray-200 text-gray-800">
          {t("projects.statusUpcoming")}
        </span>
      );
    }

    // Otherwise, treat as in progress (started or ongoing)
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-sm bg-green-100 text-green-800">
        {t("projects.statusInProgress")}
      </span>
    );
  };

  const getStatusRank = (proj) => {
    // Prefer backend-provided status when available; fall back to client-side computation
    if (proj && proj.status) {
      if (proj.status === "in_progress") return 0;
      if (proj.status === "upcoming") return 1;
      if (proj.status === "done") return 2;
      if (proj.status === "archived") return 99;
    }

    const now = new Date();
    if (proj.isDeleted) return 99; // archived: put last (but we'll filter them out)
    const start = proj.startDate ? new Date(proj.startDate) : null;
    const end = proj.endDate ? new Date(proj.endDate) : null;
    if (end && end < now) return 2; // Done
    if (start && start > now) return 1; // Upcoming
    return 0; // In progress
  };

  // Exclude archived projects and sort by: 1) last viewed (most recent first), 2) status, 3) name
  const visibleProjects = projects
    .filter((p) => !p.isDeleted)
    .slice()
    .sort((a, b) => {
      // First, sort by last viewed timestamp (most recent first)
      const aLastViewed = getProjectLastViewed(a._id);
      const bLastViewed = getProjectLastViewed(b._id);
      
      if (aLastViewed && bLastViewed) {
        // Both have been viewed - most recent first
        const timeDiff = bLastViewed.getTime() - aLastViewed.getTime();
        if (timeDiff !== 0) return timeDiff;
      } else if (aLastViewed && !bLastViewed) {
        // a has been viewed, b hasn't - a comes first
        return -1;
      } else if (!aLastViewed && bLastViewed) {
        // b has been viewed, a hasn't - b comes first
        return 1;
      }
      // Neither has been viewed (or both have) - fall through to status/name sorting
      
      // Then sort by status rank
      const ra = getStatusRank(a);
      const rb = getStatusRank(b);
      if (ra !== rb) return ra - rb;
      
      // Finally, sort alphabetically by name
      return (a.name || "").localeCompare(b.name || "");
    });

  const newProjectButton = user?.isAdmin ? (
    <NewItemButton
      onClick={() => setIsModalOpen(true)}
      label={t("projects.newProject")}
      shortLabel={t("projects.newProjectShort")}
    />
  ) : null;

  return (
    <>
      <PageHeader
        title={t("projects.title")}
        search={{
          value: search,
          onChange: (e) => {
            setSearch(e.target.value);
            setPage(1);
          },
          placeholder: t("projects.searchPlaceholder"),
          onClear: () => {
            setSearch("");
            setPage(1);
          },
        }}
        actions={newProjectButton}
      />
      <PageWrapper>

        {loading ? (
          <div className="text-center py-8">{t("projects.loading")}</div>
        ) : error ? (
          <div className="text-red-600 py-4">{error}</div>
        ) : projects.length === 0 ? (
          <div className="text-sm text-gray-600 py-4">{t("projects.noProjectsFound")}</div>
        ) : (
          <>
            {/* Project Cards Grid - Responsive: 1 column mobile, 2 columns tablet, 3 columns desktop */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
              {visibleProjects.map((p) => (
                <ProjectCard
                  key={p._id}
                  project={p}
                  user={user}
                  mapsHref={mapsHref}
                  formatAddressCity={formatAddressCity}
                  getStatusBadge={getStatusBadge}
                />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between text-sm text-gray-600">
                <div>
                  {t("projects.showingOf", { count: visibleProjects.length, total })}
                </div>
                <div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onPrev}
                    disabled={page === 1}
                    className="mr-2"
                  >
                    {t("projects.prev")}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onNext}
                    disabled={page >= totalPages}
                  >
                    {t("projects.next")}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        <AddProjectModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onAddProject={handleAddProject}
        />
      </PageWrapper>
    </>
  );
};

export default ProjectsListView;
